import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { RosterService } from '../../services/roster.service';
import { LoginService } from '../../services/login.service';
import { Roster } from '../../models/roster';
import { CreatorTileComponent } from '../creator-tile/creator-tile.component';

@Component({
  selector: 'app-public-creators',
  imports: [CommonModule, CreatorTileComponent],
  templateUrl: './public-creators.component.html',
  styleUrl: './public-creators.component.css'
})
export class PublicCreatorsComponent implements OnInit {
  creators: Partial<Roster>[] = [];
  loading = true;
  title: string = '';
  body: string = '';
  ids: string[] = [];
  linkCopied = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private rosterService: RosterService,
    private loginService: LoginService
  ) {}

  get isLoggedIn(): boolean {
    return !!this.loginService.accessToken || !!localStorage.getItem('jwt');
  }

  ngOnInit(): void {
    this.title = this.route.snapshot.queryParamMap.get('title') ?? '';
    this.body = this.route.snapshot.queryParamMap.get('body') ?? '';

    this.ids = (this.route.snapshot.queryParamMap.get('ids') ?? '')
      .split(',')
      .filter(id => id.trim().length > 0);

    this.rosterService.getPublicRosters(this.ids).subscribe({
      next: (list) => {
        const idOrder = new Map(this.ids.map((id, i) => [id, i]));
        this.creators = list
          .slice()
          .sort((a, b) => (idOrder.get(a.id) ?? 0) - (idOrder.get(b.id) ?? 0))
          .map(r => ({
          id: r.id,
          name: r.name,
          tiktokHandle: r.tikTokHandle || undefined,
          instagramHandle: r.instagramHandle || undefined,
          youtubeHandle: r.youtubeHandle || undefined,
          tikTokFollowers: r.tikTokFollowers || undefined,
          instagramFollowers: r.instagramFollowers || undefined,
          youtubeFollowers: r.youtubeFollowers || undefined,
          bioTextShort: r.bioTextShort || undefined,
          city: r.city || undefined,
          state: r.state || undefined,
          genre: r.genre || undefined,
        }));
      },
      complete: () => { this.loading = false; }
    });
  }

  removeCreator(id: string): void {
    this.creators = this.creators.filter(c => c.id !== id);
    this.ids = this.ids.filter(i => i !== id);
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { ids: this.ids.join(',') },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  // ── Card drag-to-reorder (logged-in users only), same pattern as outreach ──
  dragIndex: number | null = null;
  dropIndex: number | null = null;

  onTileDragStart(event: DragEvent, index: number): void {
    if (!this.isLoggedIn) { event.preventDefault(); return; }
    this.dragIndex = index;
    event.dataTransfer?.setData('text/plain', String(index));
    event.dataTransfer!.effectAllowed = 'move';
  }

  onDragEnd(): void {
    this.resetDragState();
  }

  onGridDragOver(event: DragEvent): void {
    if (this.dragIndex === null) return;
    event.preventDefault();
    event.dataTransfer!.dropEffect = 'move';
    this.dropIndex = this.computeDropIndex(event, event.currentTarget as HTMLElement);
  }

  onGridDragLeave(event: DragEvent): void {
    const container = event.currentTarget as HTMLElement;
    if (!container.contains(event.relatedTarget as Node)) {
      this.dropIndex = null;
    }
  }

  onGridDrop(event: DragEvent): void {
    event.preventDefault();
    if (this.dragIndex === null) { this.resetDragState(); return; }

    let insertAt = this.dropIndex ?? this.creators.length;
    const [moved] = this.creators.splice(this.dragIndex, 1);
    if (this.dragIndex < insertAt) insertAt--;
    this.creators.splice(insertAt, 0, moved);

    this.ids = this.creators.map(c => c.id).filter((id): id is string => !!id);
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { ids: this.ids.join(',') },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
    this.resetDragState();
  }

  private computeDropIndex(event: DragEvent, container: HTMLElement): number {
    const cards = Array.from(container.querySelectorAll('.tile-wrap')) as HTMLElement[];
    for (let i = 0; i < cards.length; i++) {
      const rect = cards[i].getBoundingClientRect();
      // Above the vertical center of this card → insert before it
      if (event.clientY < rect.top + rect.height / 2) return i;
      // Same row, left of horizontal center → insert before it
      if (event.clientY < rect.bottom && event.clientX < rect.left + rect.width / 2) return i;
    }
    return cards.length;
  }

  private resetDragState(): void {
    this.dragIndex = null;
    this.dropIndex = null;
  }

  copyLink(): void {
    navigator.clipboard.writeText(window.location.href).then(() => {
      this.linkCopied = true;
      setTimeout(() => { this.linkCopied = false; }, 2000);
    });
  }
}
